import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { DEFAULT_RADIUS_VALUES } from '../filters/helpers';
import { QueryParams } from '@keaze/web/common/enums';
import { FilterValue } from '@keaze/web/components/filters';
import { replaceRouteQuery } from '@keaze/web/utils/replaceRouteQuery';

type UseNotFoundExpected = {
  isMaxRadius: boolean;
  incrementBy: number;
  onClick: () => void;
};

type UseNotFound = () => UseNotFoundExpected;

type FindCurrentRadiusIdx = (radius: FilterValue) => number;

const findCurrentRadiusIdx: FindCurrentRadiusIdx = (radius) => {
  if (radius !== null) {
    return DEFAULT_RADIUS_VALUES.findIndex((value) => value === Number(radius));
  }

  return -1;
};

export const useNotFound: UseNotFound = () => {
  const router = useRouter();
  const radius = router.query[QueryParams.Radius] as string;
  const [currentRadiusIdx, setCurrentRadiusIdx] = useState<number>(
    findCurrentRadiusIdx(radius)
  );
  const maxRadiusIdx = useMemo(() => DEFAULT_RADIUS_VALUES.length - 1, []);
  const isMaxRadius = useMemo(() => currentRadiusIdx === maxRadiusIdx, [
    currentRadiusIdx,
    maxRadiusIdx,
  ]);
  const newRadius = useMemo(() => DEFAULT_RADIUS_VALUES[currentRadiusIdx + 1], [
    currentRadiusIdx,
  ]);

  const incrementBy = useMemo(() => {
    if (!newRadius) {
      return 0;
    }

    if (currentRadiusIdx === -1) {
      return DEFAULT_RADIUS_VALUES[0];
    }

    const currentRadius = DEFAULT_RADIUS_VALUES[currentRadiusIdx];

    return newRadius - currentRadius;
  }, [currentRadiusIdx, newRadius]);

  useEffect(() => {
    const currentRadius = DEFAULT_RADIUS_VALUES[currentRadiusIdx];

    if (radius && Number(radius) !== currentRadius) {
      setCurrentRadiusIdx(findCurrentRadiusIdx(radius));
    }
  }, [radius, currentRadiusIdx]);

  const handleClick = useCallback(async () => {
    if (!isMaxRadius) {
      setCurrentRadiusIdx((prevState) => prevState + 1);

      await replaceRouteQuery(router, {
        ...router.query,
        [QueryParams.Radius]: newRadius,
      });
      router.reload();
    }
  }, [router, isMaxRadius, newRadius]);

  return {
    isMaxRadius,
    incrementBy,
    onClick: handleClick,
  };
};
